/** @jsxImportSource @emotion/react */
import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Tree } from 'antd'
import type { DataNode } from 'antd/es/tree'
import type RcTree from 'rc-tree'
import { useHistory, useLocation } from 'react-router-dom'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { OrganizationTreeNode } from './hooks/useOrganizationsTree'
import { findNodePath } from './utils/treeUtils'
import { paragraphXSmall } from '../css'

interface OrganizationsTreeProps {
  data: OrganizationTreeNode[]
}

const ORG_PLACEHOLDER =
  '/images/generic_placeholders/placeholder_organization.png'

const treeContainerStyle = css`
  padding: 0 8px;
  overflow: hidden;

  // https://sportninja.atlassian.net/browse/SN-7031
  // Denys asked to return the scrollbar. If we want to remove this, just uncomment the lines below
  /* Hide scrollbar for container and all children */
  // scrollbar-width: none; /* Firefox */
  // -ms-overflow-style: none; /* IE and Edge */
  // &::-webkit-scrollbar {
  //   display: none; /* Chrome, Safari, Opera */
  // }
  // * {
  //   scrollbar-width: none;
  //   -ms-overflow-style: none;
  //   &::-webkit-scrollbar {
  //     display: none;
  //   }
  // }

  .ant-tree {
    background: transparent;
    color: ${colors.NEUTRAL_0};
  }

  .ant-tree-list-holder-inner {
    width: 100% !important;
  }

  .ant-tree-title {
    display: block;
    overflow: hidden;
    width: 100%;
  }

  // https://sportninja.atlassian.net/browse/SN-7031
  // Denys asked to return the scrollbar. If we want to remove this, just uncomment the lines below
  // .ant-tree-list-scrollbar {
  //   display: none !important;
  // }

  .ant-tree-switcher {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    color: ${colors.NEUTRAL_50};
    // remove the effect when hover
    &:hover {
      background: transparent !important;
    }
  }

  .ant-tree-switcher-leaf-line {
    display: none;
  }

  .ant-tree-node-content-wrapper {
    background: transparent !important;
    padding: 0;
    display: flex;
    align-items: center;
    min-height: 24px;
    overflow: hidden;
    flex: 1;
    min-width: 0;

    &:hover {
      background: transparent !important;
    }
  }

  .ant-tree-treenode {
    padding: 0;
    align-items: center;
    width: 100%;
    overflow: hidden;

    &:hover {
      background: transparent;
    }
  }

  .ant-tree-indent-unit {
    width: 8px;
  }
`

const treeNodeStyle = (isSelected: boolean) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 6px;
  cursor: pointer;
  border-radius: 8px;
  width: 100%;
  overflow: hidden;
  min-width: 0;
  background: ${isSelected ? colors.SECONDARY_400 : 'transparent'};
  // outline: ${isSelected ? `2px solid ${colors.PRIMARY}` : 'none'};
  outline-offset: -2px;

  &:hover {
    background: ${colors.SECONDARY_400};
  }
`

const nodeImageStyle = css`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid ${colors.NEUTRAL_0};
`

const nodeTextContainerStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const nodeNameStyle = (isSelected: boolean, hasRole: boolean) => css`
  ${paragraphXSmall}
  font-weight: ${hasRole ? 700 : 500};
  color: ${isSelected
    ? colors.PRIMARY
    : hasRole
    ? colors.NEUTRAL_0
    : colors.NEUTRAL_30};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const nodeRoleStyle = css`
  ${paragraphXSmall}
  font-weight: 400;
  color: ${colors.NEUTRAL_50};
  line-height: 1;
`

interface TreeNodeItemProps {
  node: OrganizationTreeNode
  onClick: () => void
  isSelected: boolean
}

const TreeNodeItem: React.FC<TreeNodeItemProps> = ({
  node,
  onClick,
  isSelected,
}) => {
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null
    e.currentTarget.src = ORG_PLACEHOLDER
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick()
  }

  return (
    <div css={treeNodeStyle(isSelected)} onClick={handleClick}>
      <img
        src={node.image_url || ORG_PLACEHOLDER}
        alt={node.name}
        css={nodeImageStyle}
        onError={handleImageError}
        loading='lazy'
      />
      <div css={nodeTextContainerStyle}>
        <p
          css={nodeNameStyle(
            isSelected,
            node.membership?.role_name ? true : false
          )}
        >
          {node.name}
        </p>
        {node.membership?.role_name && (
          <p css={nodeRoleStyle}>{node.membership.role_name}</p>
        )}
      </div>
    </div>
  )
}

/**
 * OrganizationsTree - Displays a hierarchical tree of user organizations
 * Uses Ant Design Tree with custom node rendering
 */
const OrganizationsTree: React.FC<OrganizationsTreeProps> = ({ data }) => {
  const history = useHistory()
  const location = useLocation()

  // Extract org ID from path like /org/{orgId} or /org/{orgId}/teams
  const currentOrgId = useMemo(() => {
    const match = location.pathname.match(/^\/org\/([^/]+)/)
    return match ? match[1] : null
  }, [location.pathname])

  // Tree ref for programmatic scrolling
  const treeRef = useRef<RcTree>(null)

  // Ref for scrolling tree container into view (sidebar scroll)
  const treeContainerRef = useRef<HTMLDivElement>(null)

  // Track pending scroll target (set when expanding, cleared after scroll)
  const pendingScrollTarget = useRef<string | null>(null)

  // Controlled expansion state
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])

  // Handle manual expansion/collapse
  const handleExpand = (keys: React.Key[]) => {
    setExpandedKeys(keys)
  }

  // Auto-expand ancestors when currentOrgId changes
  useEffect(() => {
    if (!data || !currentOrgId) {
      return
    }

    const path = findNodePath(data, currentOrgId)
    if (!path) {
      return // Node not found in tree - don't scroll sidebar
    }

    // Scroll the tree container into view (sidebar scroll)
    treeContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })

    // Set pending scroll target BEFORE expanding
    pendingScrollTarget.current = currentOrgId

    // Expand all ancestors (exclude the target node itself)
    const ancestorKeys = path.slice(0, -1)
    if (ancestorKeys.length > 0) {
      setExpandedKeys((prev) => {
        const newKeys = new Set([...prev, ...ancestorKeys])
        return Array.from(newKeys)
      })
    } else {
      // No ancestors to expand - scroll immediately
      requestAnimationFrame(() => {
        treeRef.current?.scrollTo({
          key: currentOrgId,
          align: 'top',
          offset: 8,
        })
        pendingScrollTarget.current = null
      })
    }
  }, [data, currentOrgId])

  // Scroll after tree re-renders with expanded nodes
  useEffect(() => {
    if (!pendingScrollTarget.current) {
      return
    }

    const targetKey = pendingScrollTarget.current

    // Use setTimeout to ensure tree has fully re-rendered
    const timeoutId = setTimeout(() => {
      treeRef.current?.scrollTo({
        key: targetKey,
        align: 'top',
        offset: 8,
      })
      pendingScrollTarget.current = null
    }, 50) // Small delay for virtual list to update

    return () => clearTimeout(timeoutId)
  }, [expandedKeys])

  const handleNodeClick = React.useCallback(
    (nodeId: string) => {
      history.push(`${ROUTES.ORG_ROOT}/${nodeId}`)
    },
    [history]
  )

  const transformToTreeData = React.useCallback(
    (nodes: OrganizationTreeNode[]): DataNode[] => {
      return nodes.map((node) => ({
        key: node.id,
        title: (
          <TreeNodeItem
            node={node}
            onClick={() => handleNodeClick(node.id)}
            isSelected={node.id === currentOrgId}
          />
        ),
        children: node.children
          ? transformToTreeData(node.children)
          : undefined,
        isLeaf: node.children_count === 0,
      }))
    },
    [handleNodeClick, currentOrgId]
  )

  const treeData = useMemo(
    () => transformToTreeData(data),
    [data, transformToTreeData]
  )

  return (
    <div ref={treeContainerRef} css={treeContainerStyle}>
      <Tree
        ref={treeRef}
        treeData={treeData}
        expandedKeys={expandedKeys}
        onExpand={handleExpand}
        showIcon={false}
        selectable={false}
        blockNode
        height={500}
        motion={false}
      />
    </div>
  )
}

export default OrganizationsTree
